import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Card, EmptyState, PageHeader } from '@homecare/ui';
import { MfaEnrolment } from '../features/auth/mfa-enrolment';
import { ForgotPassword, ResetPassword } from '../features/auth/password-reset';
import { SignIn } from '../features/auth/sign-in';
import { AppShell } from '../features/shell/app-shell';
import { ClientDetail } from '../features/clients/client-detail';
import { ClientImport } from '../features/clients/client-import';
import { CredentialTypesPage } from '../features/admin/credential-types';
import { ComplianceQueuePage } from '../features/credentials/compliance-queue';
import { RatesPage } from '../features/rates/rates-page';
import { OverrideReportPage } from '../features/schedule/override-report';
import { SchedulePage } from '../features/schedule/schedule-page';
import { CaregiverDetail } from '../features/caregivers/caregiver-detail';
import { CaregiverList } from '../features/caregivers/caregiver-list';
import { CaregiverNew } from '../features/caregivers/caregiver-new';
import { ClientList } from '../features/clients/client-list';
import { IntakeWizard } from '../features/clients/intake-wizard';
import { Dashboard } from '../features/shell/dashboard';
import { NAVIGATION, permissionForPath } from '../features/shell/navigation';
import { copyForPath, type PageCopy } from '../features/shell/page-copy';
import { useSession } from '../lib/session';

/**
 * Routing, and the second of the three places access is decided.
 *
 * Hiding a nav item is not access control — it is tidiness. Anyone can type a
 * URL, and the person most likely to is the one who was told last week that
 * they would be given billing access. `guarded` is what answers them, and the
 * API's `@RequirePermission` is what answers a client that ignores both.
 *
 * Three layers, and they fail in the right order: the rail declines to draw it,
 * the router declines to route to it, the server declines to serve it.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ---------------------------------------------------------------------------
// Unauthenticated
// ---------------------------------------------------------------------------

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignIn,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search['token'] === 'string' ? { token: search['token'] } : {},
});

// ---------------------------------------------------------------------------
// Authenticated
// ---------------------------------------------------------------------------

/**
 * Everything inside the shell requires a session.
 *
 * `restore()` is awaited before deciding, so a page opened from a bookmark with
 * a valid token in storage does not flash the sign-in screen on its way to the
 * dashboard.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: async ({ location }) => {
    const session = useSession.getState();

    if (session.status === 'unknown') await session.restore();

    if (useSession.getState().status !== 'signed-in') {
      throw redirect({ to: '/sign-in', search: { next: location.pathname } });
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

/**
 * A route the caller must hold a permission to reach.
 *
 * The check runs in `beforeLoad`, so an unauthorised URL never renders the page
 * at all — no flash of a screen the person is not allowed to see, and no
 * request fired from a component that was mounted for a moment.
 *
 * It answers with its own screen rather than a redirect to the dashboard. Being
 * silently bounced somewhere else is the interface refusing to say what
 * happened, and the person then tries again, twice, before asking.
 */
function guarded(path: string, heading: string, copy: PageCopy) {
  return createRoute({
    getParentRoute: () => shellRoute,
    path,
    beforeLoad: () => {
      const required = permissionForPath(path);
      if (!required) return;

      const { permissions } = useSession.getState();
      if (!permissions.includes(required)) {
        throw new PermissionDenied(required);
      }
    },
    errorComponent: ({ error }) =>
      error instanceof PermissionDenied ? <NoAccess /> : <Unexpected />,
    component: () => <SectionPage heading={heading} copy={copy} />,
  });
}

class PermissionDenied extends Error {
  constructor(readonly permission: string) {
    super(`Missing ${permission}`);
    this.name = 'PermissionDenied';
  }
}

function NoAccess(): ReactNode {
  return (
    <div className="grid max-w-3xl gap-5">
      <PageHeader title="You do not have access to this" />
      <Card>
        <p className="text-md text-ink-2 max-w-prose">
          Your role does not include this part of the console. If you think it should, ask an
          administrator at your agency to update your role.
        </p>
      </Card>
    </div>
  );
}

function Unexpected(): ReactNode {
  return (
    <div className="grid max-w-3xl gap-5">
      <PageHeader title="This screen did not finish loading" />
      <Card>
        <p className="text-md text-ink-2 max-w-prose">
          Reload the page — nothing you had open has been lost. If it happens again, tell your
          administrator which screen you were on.
        </p>
      </Card>
    </div>
  );
}

/**
 * A section of the console, empty.
 *
 * The empty state describes what belongs on the page in the reader's terms —
 * their clients, their unfilled shifts, the licence that expires Friday — and
 * says nothing about our build order. See `page-copy.ts` for the rule and the
 * test that enforces it.
 */
function SectionPage({ heading, copy }: { heading: string; copy: PageCopy }): ReactNode {
  return (
    // The same two parts every screen in this product has: the title on the
    // page, and the work in a white card over the cool background. When there
    // is work here it will be a table in that card; until then it is the empty
    // state, in the same place, so the screen does not rearrange itself the day
    // the data arrives.
    <div className="grid gap-5">
      <PageHeader title={heading} />
      <Card>
        <EmptyState title={copy.title} explanation={copy.explanation} />
      </Card>
    </div>
  );
}

/** The landing page inside the shell. */
const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: Dashboard,
});

const twoStepRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/account/two-step',
  component: MfaEnrolment,
});

/**
 * One guarded route per navigation item, built from the same table the rail is.
 *
 * Deriving both from `NAVIGATION` is what stops the two drifting: an item
 * cannot appear in the rail without a route that enforces its permission, and a
 * route cannot exist without an item that declares one.
 */
/**
 * The paths that have a real screen now.
 *
 * `guarded` builds an empty section page from `NAVIGATION` for everything that
 * does not, which is what keeps the rail and the router derived from one table.
 * A path listed here is one where that placeholder has been replaced — and it
 * is a list rather than a check for "does a component exist", because the
 * placeholder is the right answer for every path not on it and the difference
 * has to be a decision somebody made.
 */
const BUILT = new Set([
  '/clients',
  '/caregivers',
  '/credentials',
  '/rates',
  '/schedule',
  '/admin/credential-types',
]);

const sectionRoutes = NAVIGATION.flatMap((section) =>
  section.items
    .filter((item) => !BUILT.has(item.to))
    .map((item) => {
      const copy = copyForPath(item.to);

      // page-copy.spec.ts fails the build before this can happen. The throw is
      // here so that if it ever does, it happens at load with the path in the
      // message rather than as a blank page someone reports next quarter.
      if (!copy) throw new Error(`No page copy for ${item.to}`);

      return guarded(item.to, item.label, copy);
    }),
);

/**
 * Clients (§7).
 *
 * All four screens sit under `client:read` except the two that write, which
 * take `client:create` — the same permission the API demands, checked here so
 * an unauthorised URL never renders rather than rendering and then failing.
 *
 * `/clients/new` and `/clients/import` are declared **before** `/clients/$clientId`
 * so a literal segment cannot be swallowed as an id. TanStack matches static
 * before dynamic regardless, and the ordering here says so to the next reader.
 */
/**
 * The permission check every client route runs, before it renders.
 *
 * A helper for the *body* rather than for the route, because a route built by a
 * function loses its literal path and TanStack's `Link` type union goes with
 * it — `to="/clients/new"` then fails to typecheck against a union that no
 * longer contains it. The routes below are therefore spelled out.
 */
const requirePermission = (permission: string) => (): void => {
  const { permissions } = useSession.getState();
  if (!permissions.includes(permission)) throw new PermissionDenied(permission);
};

const onPermissionError = ({ error }: { error: unknown }): ReactNode =>
  error instanceof PermissionDenied ? <NoAccess /> : <Unexpected />;

const clientsListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/clients',
  beforeLoad: requirePermission('client:read'),
  errorComponent: onPermissionError,
  component: () => <ClientList />,
});

const clientsNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/clients/new',
  beforeLoad: requirePermission('client:create'),
  errorComponent: onPermissionError,
  component: () => <IntakeWizard />,
});

const clientsImportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/clients/import',
  beforeLoad: requirePermission('client:create'),
  errorComponent: onPermissionError,
  component: () => <ClientImport />,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/clients/$clientId',
  beforeLoad: requirePermission('client:read'),
  errorComponent: onPermissionError,
  component: function ClientDetailRoute() {
    const { clientId } = clientDetailRoute.useParams();
    return <ClientDetail clientId={clientId} />;
  },
});

/**
 * Caregivers (slice-02 §7).
 *
 * `/caregivers/new` before `/caregivers/$caregiverId`, for the reason the client
 * routes give: TanStack matches static before dynamic anyway, and the ordering
 * says so to the next reader.
 *
 * The roster and the record take `caregiver:read`; adding takes
 * `caregiver:create`. `caregiver:read_pay` is **not** a route permission —
 * gating a route on it would show a scheduler nothing, when what §10.4 entitles
 * them to is the whole roster without one column.
 */
const caregiversListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/caregivers',
  beforeLoad: requirePermission('caregiver:read'),
  errorComponent: onPermissionError,
  component: () => <CaregiverList />,
});

const caregiversNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/caregivers/new',
  beforeLoad: requirePermission('caregiver:create'),
  errorComponent: onPermissionError,
  component: () => <CaregiverNew />,
});

const caregiverDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/caregivers/$caregiverId',
  beforeLoad: requirePermission('caregiver:read'),
  errorComponent: onPermissionError,
  component: function CaregiverDetailRoute() {
    const { caregiverId } = caregiverDetailRoute.useParams();
    return <CaregiverDetail caregiverId={caregiverId} />;
  },
});

/*
 * The compliance queue (slice-02 session 8): HR's morning screen, at the
 * rail's "Credentials" item, which was a placeholder until now. Gated on
 * `credential:read` like the API route it calls — OWNER, ADMIN and HR in
 * §10.4. A scheduler does not hold it and does not see the item.
 */
/*
 * Rates (§6.3.3). The rail's "Rates" item opened a placeholder until slice 2
 * session 9, and GET/POST /v1/rates had no caller. `rate:read` to see them,
 * as the API demands; the form inside checks `rate:write`.
 */
const ratesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/rates',
  beforeLoad: requirePermission('rate:read'),
  errorComponent: onPermissionError,
  component: () => <RatesPage />,
});

const complianceQueueRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/credentials',
  beforeLoad: requirePermission('credential:read'),
  errorComponent: onPermissionError,
  component: () => <ComplianceQueuePage />,
});

/*
 * Credential types sit in the account menu with the other administration, not
 * on the rail. It is configuration somebody sets up once and revisits when a
 * state requirement changes — the rail is for daily work, and it is at 11 of
 * its 11-item budget.
 *
 * Its path is in `BUILT` above, which is not optional: `sectionRoutes` derives a
 * placeholder route from every `NAVIGATION` item that is not there, and two
 * routes claiming one path make the router throw at module load — a blank page
 * on every route, not just this one.
 */
const credentialTypesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/admin/credential-types',
  beforeLoad: requirePermission('credential:read'),
  errorComponent: onPermissionError,
  component: () => <CredentialTypesPage />,
});

/*
 * The schedule board (slice-03 §8), at the rail's "Schedule" item, which was
 * a placeholder until slice 3 session 8. `schedule:read`, as GET
 * /v1/schedule/board demands; booking inside it checks `schedule:create` and
 * every change `schedule:update`, as their routes do.
 *
 * The override report hangs off the board's header rather than the rail: it
 * is the weekly review of what the board did, read beside it.
 */
const scheduleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/schedule',
  beforeLoad: requirePermission('schedule:read'),
  errorComponent: onPermissionError,
  component: () => <SchedulePage />,
});

const overrideReportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/schedule/overrides',
  beforeLoad: requirePermission('schedule:read'),
  errorComponent: onPermissionError,
  component: () => <OverrideReportPage />,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  shellRoute.addChildren([
    dashboardRoute,
    twoStepRoute,
    clientsListRoute,
    clientsNewRoute,
    clientsImportRoute,
    clientDetailRoute,
    caregiversListRoute,
    caregiversNewRoute,
    caregiverDetailRoute,
    credentialTypesRoute,
    complianceQueueRoute,
    ratesRoute,
    scheduleRoute,
    overrideReportRoute,
    ...sectionRoutes,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: () => (
    <section className="mx-auto max-w-prose px-6 py-16">
      <h1 className="text-display text-ink font-bold">That page does not exist</h1>
      <p className="text-md text-ink-3 mt-2">
        Check the address, or use the sections on the left to get where you were going.
      </p>
    </section>
  ),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
